<script>
import tenantService from "@/services/tenant";

/**
 * @component TenantList
 * @description Lista de tenants del tablero de Plataforma. En el modelo
 *              "una DB por tenant" devuelve un array de 1 elemento (la Org
 *              de la DB actual); se mantiene la forma de lista para que el
 *              componente esté listo si pasamos a un modelo central.
 *              Solo accesible para `platform-admin` — el guard de UI vive en
 *              SidebarNav (oculta el item) y el backend rechaza con 403.
 */
export default {
  name: "TenantList",

  data() {
    return {
      isLoading: false,
      tenants: [],
    };
  },

  computed: {
    /**
     * @description Genera las iniciales de 2 letras del tenant a partir de
     *              su `name` o, si no tiene, de su `key`. Usadas en el avatar.
     * @param {Object} tenant
     * @returns {string}
     */
  },

  created() {
    this.load();
  },

  methods: {
    /**
     * @description Pega contra `/tenants` para traer la lista. Si falla, deja
     *              `tenants = []` y muestra el mensaje del backend al usuario.
     * @returns {Promise<void>}
     */
    async load() {
      this.isLoading = true;
      try {
        const { docs } = await tenantService.list();
        this.tenants = docs || [];
      } catch (err) {
        this.$message.error(
          err?.response?.data?.message ||
            "No pudimos traer la lista de tenants."
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Iniciales del avatar del tenant. 2 letras del name o key.
     * @param {Object} tenant
     * @returns {string}
     */
    getInitials(tenant) {
      const source = tenant.name || tenant.key || "??";
      const parts = source.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return source.slice(0, 2).toUpperCase();
    },

    /**
     * @description Label legible del preset (slug) para mostrar en la tabla.
     * @param {string} preset
     * @returns {string}
     */
    presetLabel(preset) {
      const map = {
        "consultorio-puro": "Consultorio puro",
        "consultorio-ws": "Consultorio + WS externo",
        "ooss-credencial": "OS afiliados + credencial",
        "ooss-clinica-programas": "OS + clínica + programas",
        full: "Full ecosistema",
      };
      return map[preset] || preset || "—";
    },

    /**
     * @description Cuenta cuántas features tiene apagadas (false explícito)
     *              vs el total que tiene seteadas. Sirve como indicador
     *              rápido en la tabla.
     * @param {Object} tenant
     * @returns {{ off: number, on: number, total: number }}
     */
    featureCounts(tenant) {
      const features = tenant.features || {};
      const entries = Object.entries(features);
      const off = entries.filter(([, v]) => v === false).length;
      const on = entries.filter(([, v]) => v === true).length;
      return { off, on, total: entries.length };
    },

    /**
     * @description Navega al detalle del tenant.
     * @param {string} id
     */
    goToDetail(id) {
      this.$router.push({ name: "platform-tenant-detail", params: { id } });
    },
  },
};
</script>

<template lang="pug">
section.tenant-list-page
  .page-header
    h1.page-title Tenants
    p.page-subtitle Configuración de features, branding e integraciones por tenant.

  .table-card
    spinner(v-if="isLoading")
    .empty-state(v-else-if="!tenants.length")
      p No hay tenants para mostrar en esta DB.
    table.data-table(v-else)
      thead
        tr
          th Tenant
          th Preset
          th Estado
          th(style="text-align:right") Features apagadas
          th Creado
          th
      tbody
        tr.data-table__row(
          v-for="tenant in tenants"
          :key="tenant._id"
          @click="goToDetail(tenant._id)"
        )
          td
            .tenant-name
              .tenant-avatar {{ getInitials(tenant) }}
              .tenant-meta
                strong {{ tenant.name || tenant.key }}
                small {{ tenant.domain || tenant.key }}
          td
            span.preset-pill {{ presetLabel(tenant.preset) }}
          td
            span.status(:class="`status--${tenant.status || 'active'}`")
              | {{ tenant.status === 'suspended' ? 'Suspendido' : 'Activo' }}
          td(style="text-align:right")
            span.feature-counts(:class="{ 'has-off': featureCounts(tenant).off > 0 }")
              | {{ featureCounts(tenant).off }} / {{ featureCounts(tenant).total || '—' }}
          td.muted
            | {{ tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString('es-AR') : '—' }}
          td.actions-cell
            a(@click.stop="goToDetail(tenant._id)") Configurar →
</template>

<style lang="scss" scoped>
.tenant-list-page {
  padding: 24px 32px;
  font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont,
    sans-serif;
}
.page-header {
  margin-bottom: 24px;
}
.page-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 800;
  color: #002d5e;
  letter-spacing: -0.01em;
}
.page-subtitle {
  margin: 0;
  font-size: 13.5px;
  color: #64748b;
}
.table-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}
.empty-state {
  padding: 48px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.data-table thead th {
  text-align: left;
  padding: 14px 16px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #64748b;
  text-transform: uppercase;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}
.data-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
}
.data-table tbody tr:hover {
  background: #f8fafc;
}
.data-table tbody td {
  padding: 14px 16px;
  vertical-align: middle;
  color: #334155;
}
.tenant-name {
  display: flex;
  align-items: center;
  gap: 12px;
}
.tenant-avatar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #002d5e;
  color: #fff;
  font-weight: 800;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tenant-meta strong {
  display: block;
  color: #002d5e;
  font-weight: 700;
  font-size: 14.5px;
}
.tenant-meta small {
  display: block;
  color: #64748b;
  font-size: 12px;
  margin-top: 2px;
}
.preset-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef3fa;
  color: #0a3d7a;
  font-size: 12px;
  font-weight: 700;
}
.status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
}
.status::before {
  content: "";
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.status--active {
  color: #047857;
}
.status--active::before {
  background: #10b981;
}
.status--suspended {
  color: #b45309;
}
.status--suspended::before {
  background: #f59e0b;
}
.feature-counts {
  font-variant-numeric: tabular-nums;
  color: #475569;
  font-weight: 600;
}
.feature-counts.has-off {
  color: #b45309;
}
.muted {
  color: #64748b;
  font-size: 13px;
}
.actions-cell {
  text-align: right;
}
.actions-cell a {
  color: #0a3d7a;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
}
</style>
